import { $, must } from './dom.ts';
import type { AssignmentConfig, SiteMetadata, TestResults } from './evaluator-types.ts';
import { describe, fetchOk } from './fetch-helpers.ts';
import {
  type DataFile,
  type Message,
  removeReplTab,
  type TerminalBackend,
  TerminalEngine,
} from './terminal-engine.ts';

/*
 * Interactive Python for the browser, backing the tabbed right-hand pane of an
 * interactive coding assignment (layouts/py-terminal.njk):
 *
 *   - "Terminal" tab — runs the student's whole program (the editor contents) in
 *     a real xterm.js terminal: input() blocks for typed lines, print() streams
 *     out live, open() reads files preloaded into Pyodide's MEMFS.
 *   - "REPL" tab — a standalone Python prompt (Python's own code.interact())
 *     running in its own Pyodide worker, independent of the editor.
 *
 * Both tabs are a TerminalEngine (modules/terminal-engine.ts — the xterm setup
 * and readline layer, shared with interactive-java.ts) wired to a
 * PyodideBackend: a Pyodide worker (python-repl-worker.ts) plus a
 * SharedArrayBuffer handshake so input() can block synchronously. The only
 * behavioural difference between the tabs is what Ctrl-C does and what runs
 * (editor code vs. the REPL driver). `InteractivePython` is the evaluator
 * index.ts wires up (it owns the program terminal and implements the execute()
 * contract) and also lazily spins up the REPL the first time its tab is opened.
 */

// stdinControl[STATE] handshake values — must match python-repl-worker.ts.
const STATE = 0;
const LEN = 1;
const READY = 1;
const EOF = 2;

const STDIN_DATA_BYTES = 1 << 16; // 64 KiB — max length of one input line

const encoder = new TextEncoder();

// The "program" the REPL tab runs: Python's own interactive console.
const REPL_DRIVER = [
  'import sys, code',
  'code.interact(',
  '    banner=f"Python {sys.version.split()[0]} (Pyodide) — Ctrl-D restarts, Ctrl-C cancels a line.",',
  '    exitmsg="",',
  ')',
].join('\n');

type Kind = 'program' | 'repl';

// Messages from python-repl-worker.ts (matched to its postMessage calls).
type ReplWorkerMessage =
  | { type: 'ready' }
  | { type: 'stdout' | 'stderr'; chunk: ArrayBuffer }
  | { type: 'stdin' }
  | { type: 'done' }
  | { type: 'fatal'; message: string };

/*
 * One Pyodide worker with cooked-mode line editing on the main thread and
 * synchronous stdin over a SharedArrayBuffer.
 */
class PyodideBackend implements TerminalBackend {
  kind: Kind;
  engine!: TerminalEngine;

  worker: Worker | null = null;
  readyResolvers: Array<() => void> = [];

  stdinControl!: Int32Array;
  stdinData!: Uint8Array;
  interrupt!: Int32Array;

  lineQueue: string[] = [];
  awaitingInput = false;
  eofLatched = false; // once EOF is sent, later reads in the same run also get EOF

  // The assignment's other editor tabs (filename -> content), set per run by
  // the evaluator's execute(). The worker writes them into Pyodide's
  // filesystem before the run so the student's code can import them.
  otherFiles: Record<string, string> = {};

  constructor(kind: Kind) {
    this.kind = kind;
  }

  attach(engine: TerminalEngine) {
    this.engine = engine;

    // SharedArrayBuffer is only available in a cross-origin-isolated page.
    if (typeof SharedArrayBuffer === 'undefined' || !self.crossOriginIsolated) {
      this.stdinControl = new Int32Array(2);
      this.stdinData = new Uint8Array(STDIN_DATA_BYTES);
      this.interrupt = new Int32Array(1);
      engine.disable(
        'This page is not cross-origin isolated, so interactive',
        'Python (which needs SharedArrayBuffer) is unavailable.',
      );
      return;
    }

    this.stdinControl = new Int32Array(new SharedArrayBuffer(2 * 4));
    this.stdinData = new Uint8Array(new SharedArrayBuffer(STDIN_DATA_BYTES));
    this.interrupt = new Int32Array(new SharedArrayBuffer(4));

    engine.term.writeln('Loading Python…');
    this.startWorker();
  }

  startWorker() {
    this.engine.state = 'loading';
    const w = new Worker('/js/python-repl-worker.js', { type: 'module' });
    w.onmessage = (e: MessageEvent) => this.onWorkerMessage(e.data);
    w.onerror = (e: ErrorEvent) => {
      console.log('Python repl worker error', e);
      this.engine.message('Python failed to load.', undefined, 'error');
    };
    this.worker = w;
    w.postMessage({
      type: 'init',
      stdinControl: this.stdinControl.buffer,
      stdinData: this.stdinData.buffer,
      interrupt: this.interrupt.buffer,
    });
  }

  whenReady(): Promise<void> {
    if (this.engine.state !== 'loading') return Promise.resolve();
    return new Promise((resolve) => this.readyResolvers.push(resolve));
  }

  onWorkerMessage(msg: ReplWorkerMessage) {
    const engine = this.engine;
    switch (msg.type) {
      case 'ready': {
        engine.state = 'idle';
        // If a run is already queued (someone hit Run while Python loaded), let it
        // take over rather than flashing the idle hint.
        const pendingRun = this.readyResolvers.length > 0;
        for (const r of this.readyResolvers.splice(0)) r();
        engine.message('Python ready.', 1500);
        // Replace the "Loading Python…" line so the terminal doesn't look stuck.
        // (The REPL replaces it with its own banner when it starts.)
        if (this.kind === 'program' && !pendingRun) {
          engine.term.reset();
          engine.term.writeln('\x1b[90mPython ready — click ▶ Run to run your program.\x1b[0m');
        }
        break;
      }
      case 'stdout':
      case 'stderr':
        // Both streams render to the one terminal; input()'s prompt arrives on
        // stderr (isatty), so we must show stderr too.
        engine.term.write(new Uint8Array(msg.chunk));
        break;
      case 'stdin':
        if (this.eofLatched) {
          // code.interact() (and some programs) read once more after EOF; keep
          // returning EOF so they finish instead of hanging.
          this.deliverEOF();
        } else if (this.lineQueue.length > 0) {
          this.deliverLine(must(this.lineQueue.shift(), 'queued stdin line'));
        } else {
          this.awaitingInput = true;
          engine.term.focus();
        }
        break;
      case 'done':
        engine.finishRun();
        break;
      case 'fatal':
        engine.term.writeln(`\x1b[31m${msg.message}\x1b[0m`);
        engine.finishRun();
        if (this.worker) this.worker.terminate();
        this.startWorker();
        break;
    }
  }

  start(code: string, dataFiles: DataFile[]) {
    this.lineQueue = [];
    this.awaitingInput = false;
    this.eofLatched = false;
    must(this.worker, 'python worker').postMessage({
      type: 'run',
      id: Date.now(),
      code,
      dataFiles,
      otherFiles: this.otherFiles,
    });
  }

  sendLine(line: string) {
    if (this.awaitingInput) {
      this.awaitingInput = false;
      this.deliverLine(line);
    } else {
      this.lineQueue.push(line);
    }
  }

  // Write one line's bytes (no trailing newline — Pyodide appends one) into the
  // shared buffer and wake the worker.
  deliverLine(text: string) {
    const bytes = encoder.encode(text);
    const n = Math.min(bytes.length, this.stdinData.length);
    this.stdinData.set(bytes.subarray(0, n));
    Atomics.store(this.stdinControl, LEN, n);
    Atomics.store(this.stdinControl, STATE, READY);
    Atomics.notify(this.stdinControl, STATE);
  }

  sendEOF() {
    this.deliverEOF();
  }

  deliverEOF() {
    this.awaitingInput = false;
    this.eofLatched = true;
    Atomics.store(this.stdinControl, STATE, EOF);
    Atomics.notify(this.stdinControl, STATE);
  }

  onCtrlC() {
    const engine = this.engine;
    if (this.kind === 'repl') {
      // Cancel the current line (or interrupt a running computation) but keep the
      // REPL alive — like Ctrl-C at a real Python prompt.
      engine.term.write('\r\n');
      if (!this.awaitingInput) Atomics.store(this.interrupt, 0, 2); // SIGINT
    } else if (this.awaitingInput) {
      // Program blocked on input(): the only way to interrupt a blocked read is
      // to tear it down.
      engine.term.writeln('\r\n\x1b[90m— stopped —\x1b[0m');
      this.hardStop();
    } else {
      engine.term.write('\r\n');
      Atomics.store(this.interrupt, 0, 2); // SIGINT → KeyboardInterrupt
    }
  }

  // Graceful stop: interrupt a compute loop with SIGINT (keeps the worker warm).
  // If the program is blocked in input(), SIGINT can't fire (no Python is
  // running), so fall back to a hard restart.
  stop() {
    if (this.awaitingInput) {
      this.engine.term.writeln('\x1b[90m— stopped —\x1b[0m');
      this.hardStop();
    } else {
      this.engine.message('Interrupting…');
      Atomics.store(this.interrupt, 0, 2);
    }
  }

  // Hard stop: throw away the worker (and its Pyodide) and start a fresh one.
  hardStop() {
    if (this.worker) this.worker.terminate();
    this.awaitingInput = false;
    this.engine.finishRun();
    if (this.engine.usable) {
      this.engine.term.writeln('Loading Python…');
      this.startWorker();
    }
  }
}

const pyTerminal = (mount: string, backend: PyodideBackend, message: Message): TerminalEngine =>
  new TerminalEngine(
    mount,
    backend,
    message,
    backend.kind === 'program' ? '\x1b[90m— program finished —\x1b[0m' : null,
  );

/*
 * The evaluator index.ts instantiates. Owns the program terminal (and the
 * execute() contract) plus the tab switching and the lazily-started REPL.
 */
class InteractivePython {
  config: AssignmentConfig;
  programBackend: PyodideBackend;
  program: TerminalEngine;
  repl: TerminalEngine | null = null;

  stopButton: HTMLButtonElement | null;
  restartButton: HTMLButtonElement | null;

  constructor(
    config: AssignmentConfig,
    message: Message,
    _username: string,
    _metadata: SiteMetadata,
  ) {
    this.config = config;
    if (config.replTab === false) removeReplTab();
    this.programBackend = new PyodideBackend('program');
    this.program = pyTerminal('#terminal', this.programBackend, message);

    this.stopButton = $('.terminal-tabs .stop-code') as HTMLButtonElement | null;
    if (this.stopButton) this.stopButton.onclick = () => this.program.stop();
    this.restartButton = $('.terminal-tabs .restart-repl') as HTMLButtonElement | null;
    if (this.restartButton) this.restartButton.onclick = () => this.repl?.hardStop();

    for (const tab of document.querySelectorAll<HTMLElement>('.terminal-tabs .tab')) {
      tab.onclick = () => this.showTab(must(tab.dataset.tab, 'data-tab on terminal tab'));
    }
  }

  showTab(name: string) {
    for (const v of document.querySelectorAll('.terminal-view')) {
      v.classList.toggle('active', (v as HTMLElement).dataset.view === name);
    }
    for (const t of document.querySelectorAll('.terminal-tabs .tab')) {
      t.classList.toggle('active', (t as HTMLElement).dataset.tab === name);
    }
    if (this.stopButton) this.stopButton.hidden = name !== 'terminal';
    if (this.restartButton) this.restartButton.hidden = name !== 'repl';

    if (name === 'repl' && !this.repl) {
      // Create + start the REPL only when first opened (its container is now
      // visible, so the terminal sizes correctly, and we don't pay for a second
      // Pyodide instance unless it's actually used).
      this.repl = pyTerminal('#repl-terminal', new PyodideBackend('repl'), this.program.message);
      this.startReplLoop();
    }

    const active = name === 'repl' ? this.repl : this.program;
    requestAnimationFrame(() => {
      active?.refit();
      active?.focus();
    });
  }

  // Keep a Python prompt running on the REPL tab: when it exits (Ctrl-D / exit(),
  // or a Restart), start a fresh one.
  startReplLoop() {
    this.repl?.run(REPL_DRIVER, []).then(() => {
      if (!this.repl?.usable) return;
      this.repl.term.writeln('\x1b[90m[restarting REPL]\x1b[0m');
      this.startReplLoop();
    });
  }

  // A missing data file is a visible "Couldn't load <file> (404)" (the
  // run rejects; index.ts shows it), never a 404 page written into the
  // program's working directory as data.
  async #fetchDataFiles(): Promise<DataFile[]> {
    const files: string[] = this.config.dataFiles ?? [];
    return Promise.all(
      files.map(async (f) => {
        const res = await fetchOk(new URL(f, window.location.href)).catch((e) => {
          throw new Error(describe(e, `load ${f}`));
        });
        return {
          name: must(f.split('/').pop(), `file name in ${f}`),
          data: await res.arrayBuffer(),
        };
      }),
    );
  }

  // The evaluator contract index.ts calls: run the editor code in the program
  // terminal and resolve (with no test results) when it finishes.
  async execute(
    code: string,
    _filename: string,
    others: { file: string; content: string }[] = [],
  ): Promise<TestResults> {
    const dataFiles = await this.#fetchDataFiles();
    this.programBackend.otherFiles = Object.fromEntries(others.map((o) => [o.file, o.content]));
    await this.program.run(code, dataFiles);
    return Object.create(null);
  }
}

const evaluator = (
  config: AssignmentConfig,
  message: Message,
  username: string,
  metadata: SiteMetadata,
) => new InteractivePython(config, message, username, metadata);

export default evaluator;
